import type { RemovableRef } from '@vueuse/core';
import type { MaybeRefOrGetter, WatchHandle } from 'vue';
import type { GlobalCollectionId, JsonObject, Uuid } from '#layers/data-model/public';
import { debouncedWatch, until, useSessionStorage } from '@vueuse/core';
import { klona } from 'klona';
import { computed, ref, toValue, watch } from 'vue';
import { useAppConfig } from '#app/config';
import { useStore } from '#imports';
import { canAccessOwnKeyStorage, canAccessSharedKeyStorage, useEntitlement } from '#layers/access/public';
import { isDeepEqual, mapValues, pick, storageKey } from '#layers/base/public';

export interface UsePersistedStateOptions {
	/**
	 * The associated `collectionId`. If set, this state will be associated with the collection and automatically
	 * cleaned up if the collection gets deleted.
	 */
	collectionId?: MaybeRefOrGetter<GlobalCollectionId | null>;
	/**
	 * The associated (root) `blockId`. If set, this state will be associated with the root block and automatically
	 * cleaned up if the block gets deleted.
	 */
	blockId?: MaybeRefOrGetter<Uuid | null>;
	/**
	 * The associated user ID. If set, this state will be associated with the user and automatically
	 * cleaned up if the user gets deleted. Additionally, it is ensured that the state is protected by system permissions.
	 */
	ownerId?: MaybeRefOrGetter<Uuid | null>;

	/**
	 * Toggle the fetching and updating behavior of the persisted state
	 *
	 * @default true
	 */
	enabled?: MaybeRefOrGetter<boolean>;

	/**
	 * Write the default value to the storage when it does not exist
	 *
	 * @default true
	 */
	writeDefaults?: boolean;

	/**
	 * Optional callback function that is invoked when an error occurs interacting with the API.
	 *
	 * @param error The error object or value that was caught or triggered.
	 */
	onError?: (error: unknown) => void;
}

/**
 * Creates a stateful value that is persisted to `MonospaceKeyStorage` using a key.
 * The state reactively updates when the persisted value changes.
 *
 * When the user lacks the keyStorage entitlement the state degrades to `sessionStorage`
 * so local preferences survive without hitting the API.
 *
 * The storage *backend* (plain `ref` vs `sessionStorage`) is bound once at setup: Vue cannot
 * retarget an already-bound ref, so a live entitlement flip does not move existing state between
 * sinks — a component remount is the refresh mechanism (callers instantiate this per block render,
 * so a workspace switch tears down and re-runs the composable). The reactive bits that *can* change
 * in place — the write gate (`keyStorageEnabled`), the session write gate (`persistToSession`),
 * the own-vs-shared entitlement check, and the session key — are all kept reactive below.
 *
 * @param {MaybeRefOrGetter<string>} key - The unique identifier for the state to persist.
 * @param {MaybeRefOrGetter<T>} defaults - The default value for the state if a persisted value does not already exist or is loading.
 * @param {UsePersistedStateOptions} [options] - Additional configuration options.
 *
 * @return {RemovableRef<T>} - A reactive reference to the persisted state that can be updated or reset as needed.
 */
export function usePersistedState<T>(
	key: MaybeRefOrGetter<string>,
	defaults: MaybeRefOrGetter<T>,
	options?: UsePersistedStateOptions,
): RemovableRef<T> {
	const {
		enabled = true,
		writeDefaults = true,
		onError,
	} = options ?? {};

	const store = useStore();
	const { can } = useEntitlement();
	const resolvedKey = computed(() => toValue(key));

	// Re-evaluate the keyStorage READ entitlement reactively so it settles with the descriptor
	// (cold start) and re-keys when `ownerId` resolves. Mapping assumes `ownerId === currentUser.id | null`
	// (true for all current callers — see `use-block-storage-key.ts`): an owned key needs `accessOwn`,
	// a shared key `accessShared`.
	const canUseKeyStorage = computed(() => can(toValue(options?.ownerId) != null ? canAccessOwnKeyStorage : canAccessSharedKeyStorage));

	// `workspaceSlug` is a setup-time snapshot: a workspace switch remounts the block, so it is invariant
	// for this composable instance. Without it there is no stable namespace, so the degrade path falls
	// back to a plain in-memory ref rather than serializing under a `:null:` key shared across workspaces.
	const workspaceSlug = useAppConfig().monospace.workspaceSlug;
	const sessionStorageKey = computed(() => storageKey(`ps:${workspaceSlug}:${resolvedKey.value}`));

	const keyStorageEnabled = computed(() => canUseKeyStorage.value && toValue(enabled));
	// Mirror the keyStorage write gate for the degrade path: only touch session storage while
	// the user is unentitled, persistence is enabled, and we have a stable namespace.
	const persistToSession = computed(() => !canUseKeyStorage.value && toValue(enabled) && workspaceSlug != null);

	const { data: serverState, loading } = store.MonospaceKeyStorage.query((q) => q.first(keyStorageEnabled.value
		? {
				key: resolvedKey.value,
			}
		: { enabled: false }));

	// Entitled (or session-disabled): keep the plain-ref flow. Degraded + enabled: back the state
	// with sessionStorage, driven by the computed key so vueuse re-binds on key changes, and thread
	// `onError` so private-mode/quota failures surface instead of being swallowed by the default.
	const state = (persistToSession.value
		? useSessionStorage(sessionStorageKey, toValue(defaults), { onError })
		: ref(toValue(defaults))) as RemovableRef<T>;

	/**
	 * Keep track of ongoing writes to prevent sending racing requests.
	 */
	let writing = false;
	/**
	 * If a write is requested while another is ongoing, we store the value here to be written once the ongoing one is finished
	 * so we always end up with the latest value being sent.
	 */
	let writingQueued: { value: T | null } | null = null;

	const { pause: pauseWatch, resume: resumeWatch } = debouncedWatch([state, keyStorageEnabled], ([newState]) => {
		if (keyStorageEnabled.value) {
			write(klona(newState));
		}
	}, { deep: true, debounce: 500 }) as WatchHandle;

	watch([serverState, keyStorageEnabled], ([result]) => {
		if (
			!keyStorageEnabled.value
			|| !result
			|| result.key !== resolvedKey.value
			|| isDeepEqual(result.value, state.value)
			|| writing
		) {
			return;
		}

		pauseWatch();
		state.value = klona(result.value) as T;
		resumeWatch();
	}, { immediate: true, deep: true });

	(async () => {
		await until(loading).toBe(false);

		if (!serverState.value && writeDefaults && keyStorageEnabled.value) {
			write(state.value);
		}
	})();

	return state;

	async function create(initialValue: T) {
		try {
			const additionalOptions = options ? mapValues(pick(options, ['collectionId', 'blockId', 'ownerId']), toValue) : {};
			const nonNullOptions = Object.fromEntries(Object.entries(additionalOptions).filter(([, value]) => value !== null));

			await store.MonospaceKeyStorage.create({
				key: resolvedKey.value,
				value: initialValue as JsonObject,
				...nonNullOptions,
			});
		}
		catch (error) {
			onError?.(error);
		}
	}

	async function write(value: T | null) {
		if (writing) {
			writingQueued = { value };
			return;
		}

		writing = true;

		try {
			if (value !== null && !serverState.value) {
				await create(value);
			}
			else if (value !== null && !isDeepEqual(value, serverState.value!.value)) {
				await store.MonospaceKeyStorage.update({
					value,
				}, {
					key: resolvedKey.value,
				});
			}
			else if (value === null) {
				await store.MonospaceKeyStorage.delete(resolvedKey.value);
			}
		}
		catch (error) {
			onError?.(error);
		}

		writing = false;

		if (writingQueued !== null) {
			const queuedValue = writingQueued.value;
			writingQueued = null;
			return write(queuedValue);
		}
	}
}
